import classcat from "classcat"
import React from "react"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./BudgetEditor.module.css"

import { formatCurrency } from "~/helpers/CurrencyHelper"
import { formatMinutesToDays } from "~/helpers/budgetHelper"
import { getDefaultRate } from "~/helpers/rate-helpers"
import { formatMinuteToHours } from "~/helpers/time"

import BudgetItemRow from "~/forms/ProjectBudgetForm/RoleBudget/BudgetItemRow"
import OtherExpensesRow from "~/forms/ProjectBudgetForm/RoleBudget/OtherExpensesRow"
import TableInfo from "~/forms/ProjectBudgetForm/TableInfo"

import BudgetTableHeader from "../BudgetTableHeader"
import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import { getUnallocatedRoles } from "../projectBudgetHelpers"
import RoleAdder from "../RoleAdder"
import { RateCard } from "../types"

type Props = {
  rateCard: RateCard
  defaultFullTimeMinutes: number
  onAddRole: (id: number) => void
  onShowAllRoles: () => void
}

const BudgetEditor = (props: Props) => {
  const { rateCard, defaultFullTimeMinutes, onAddRole, onShowAllRoles } = props

  const {
    totalAllocated,
    hideFinancials,
    categorizedRoles,
    showNonBillableView,
    showDayView,
    allRoles,
  } = useProjectBudgetFormContext()

  const unallocatedRoles = getUnallocatedRoles(
    allRoles,
    categorizedRoles.people,
  )

  const expenseRole = categorizedRoles.expenses

  const totalMinutes = categorizedRoles.people.reduce(
    (prev, curr) =>
      curr.estimatedMinutes ? prev + curr.estimatedMinutes : prev,
    0,
  )
  const showTotal = !!totalAllocated || !!totalMinutes

  return (
    <div className={commonStyles.budgetEditorWrapper}>
      <BudgetTableHeader />
      <div className={commonStyles.tableBody}>
        {categorizedRoles.people.map((role) => {
          const rateCardRate = getDefaultRate(rateCard, role.id)
          const canDelete = !role.isScheduled
          return (
            <BudgetItemRow
              key={role.id}
              budgetedRole={role}
              rateCardRate={rateCardRate}
              defaultFullTimeMinutes={defaultFullTimeMinutes}
              canDelete={canDelete}
              deleteTooltip={
                canDelete
                  ? `Remove budget allocation for "${role.name}"`
                  : `Roles that have been added to the project cannot be removed from the budget allocation.`
              }
            />
          )
        })}
        {!showNonBillableView && !hideFinancials && expenseRole && (
          <OtherExpensesRow key={expenseRole.id} />
        )}
      </div>
      {unallocatedRoles.length > 0 && (
        <div className={commonStyles.tableWidget}>
          <RoleAdder
            placeholder="Add Roles and Expenses"
            dataTestSuffix="budget"
            roles={unallocatedRoles}
            onAddRole={onAddRole}
            onShowAllRoles={onShowAllRoles}
          />
        </div>
      )}
      {showTotal && (
        <TableInfo
          sticky
          className={classcat([
            commonStyles.totalsWrapper,
            styles.totalsWrapper,
            {
              [styles.nbView]: showNonBillableView,
            },
          ])}
          offset={0}
          dataTest="budget-allocation"
        >
          <div className={styles.textAlignLeft}>Role Budget</div>
          <div key="time" data-test="time-total">
            {showDayView
              ? formatMinutesToDays(totalMinutes, defaultFullTimeMinutes)
              : formatMinuteToHours(totalMinutes)}
          </div>
          {!showNonBillableView && (
            <div key="money" data-test="budget-total">
              {formatCurrency(totalAllocated)}
            </div>
          )}
        </TableInfo>
      )}
    </div>
  )
}

export default BudgetEditor
